import React, { useState } from 'react';
import { Box, Drawer, useMediaQuery, useTheme } from '@mui/material';

import { StepInspector } from './StepInspector';
import { TriggerInspector } from './TriggerInspector';
import { CanvasReach, TRIGGER_KEY, WorkflowCanvas } from './WorkflowCanvas';
import { StepDraft, WorkflowSettings } from './workflowModel';

/**
 * The canvas and, beside it, the panel for whatever is picked on it: the
 * trigger or a step. With nothing picked the side shows `aside` (the
 * workflow's overview and simulation on its page, a guide on a new one). On a
 * narrow screen the panel slides up from the bottom instead.
 */

interface WorkflowBuilderProps {
  settings: WorkflowSettings;
  onSettings: (settings: WorkflowSettings) => void;
  steps: Array<StepDraft>;
  onSteps: (steps: Array<StepDraft>) => void;
  stepsEditable: boolean;
  settingsEditable: boolean;
  /** Why the steps or settings are fixed, said in the panel. */
  stepsLockedReason?: string;
  settingsLockedReason?: string;
  problems: Map<number, string>;
  /** Show the settings' problems (after a save was tried). */
  showErrors: boolean;
  enrolled?: number;
  reach?: CanvasReach | null;
  aside?: React.ReactNode;
  /** Opens the trigger's panel at once: a new workflow starts there. */
  initialSelected?: string | null;
}

export const WorkflowBuilder = ({
  settings,
  onSettings,
  steps,
  onSteps,
  stepsEditable,
  settingsEditable,
  stepsLockedReason,
  settingsLockedReason,
  problems,
  showErrors,
  enrolled,
  reach,
  aside,
  initialSelected = null,
}: WorkflowBuilderProps) => {
  const theme = useTheme();
  const wide = useMediaQuery(theme.breakpoints.up('lg'), { noSsr: true });
  // On a narrow screen the panel is a drawer over the page: it opens on a tap, never by itself.
  const [selected, setSelected] = useState<string | null>(wide ? initialSelected : null);

  const index = steps.findIndex((s) => s.key === selected);
  const step = index >= 0 ? steps[index] : null;
  const close = () => setSelected(null);

  const panel =
    selected === TRIGGER_KEY ? (
      <TriggerInspector
        settings={settings}
        editable={settingsEditable}
        lockedReason={settingsEditable ? undefined : settingsLockedReason}
        showErrors={showErrors}
        onChange={onSettings}
        onClose={close}
      />
    ) : step ? (
      <Box sx={{ display: 'grid', gap: 1.5 }}>
        {!stepsEditable && stepsLockedReason && (
          <Box sx={{ p: 1.25, borderRadius: 2, bgcolor: '#fff7ed', color: '#9a3412', fontSize: '0.8125rem' }}>{stepsLockedReason}</Box>
        )}
        <StepInspector
          key={step.key}
          step={step}
          index={index}
          editable={stepsEditable}
          onChange={(next) => onSteps(steps.map((s) => (s.key === next.key ? next : s)))}
          onClose={close}
        />
      </Box>
    ) : null;

  return (
    <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: 'minmax(0, 1fr)', lg: 'minmax(0, 1fr) 380px' }, alignItems: 'start' }}>
      <WorkflowCanvas
        settings={settings}
        steps={steps}
        editable={stepsEditable}
        selected={selected}
        onSelect={setSelected}
        onChange={onSteps}
        problems={problems}
        enrolled={enrolled}
        reach={reach}
      />

      {wide ? (
        <Box sx={{ position: 'sticky', top: 16, display: 'grid', gap: 2, maxHeight: 'calc(100vh - 32px)', overflowY: 'auto' }}>{panel ?? aside}</Box>
      ) : (
        <>
          {aside}
          <Drawer anchor="bottom" open={!!panel} onClose={close} PaperProps={{ sx: { maxHeight: '85vh', borderTopLeftRadius: 16, borderTopRightRadius: 16, p: 1 } }}>
            {panel}
          </Drawer>
        </>
      )}
    </Box>
  );
};
